import { apiGet, ApiError } from './client';

export type AuthUser = {
  id: number;
  email: string;
  name?: string;
  role: 'admin' | 'fbc' | 'franchisee';
  afNumbers?: string[];
  /** Set when an admin is impersonating this user. */
  impersonator?: { id: number; email: string; name?: string };
};

async function postJson<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data: T | { error?: string };
  try {
    data = await res.json();
  } catch {
    data = {} as T;
  }
  if (!res.ok) {
    const detail = (data as { error?: string }).error ?? `${res.status} ${res.statusText}`;
    throw new ApiError(detail, res.status);
  }
  return data as T;
}

export type CheckResponse =
  | { exists: false }
  | { exists: true; hasPassword: boolean; name?: string; role?: AuthUser['role'] };

export type LoginResponse =
  | { user: AuthUser }
  | { needsSetup: true; email: string };

export const authApi = {
  check: (email: string) => postJson<CheckResponse>('/api/auth/check', { email }),
  login: (email: string, password: string) =>
    postJson<LoginResponse>('/api/auth/login', { email, password }),
  sendSetup: (email: string) => postJson<{ success: boolean }>('/api/auth/send-setup', { email }),
  forgot: (email: string) => postJson<{ success: boolean }>('/api/auth/forgot', { email }),
  setup: (token: string, password: string, name?: string) =>
    postJson<{ success: boolean; user: AuthUser }>('/api/auth/setup', { token, password, name }),
  reset: (token: string, password: string) =>
    postJson<{ success: boolean }>('/api/auth/reset', { token, password }),
  logout: () => postJson<{ success: boolean }>('/api/auth/logout', {}),
  me: () => apiGet<{ user: AuthUser }>('/api/auth/me'),
  stopImpersonating: () => postJson<{ user: AuthUser }>('/api/auth/stop-impersonating', {}),
};
